/**
 * Typed client for the customer workshop API (`api/db/workshop/*`).
 *
 * A *workshop* is one customer engagement: an account, a type (`safe-risk`, `safe-architect`, and
 * whatever comes next), an owner, a status, and a bag of state. It is the thing a SAFE assessment
 * now hangs off, and it exists because the assessment used to live in one browser's localStorage —
 * invisible to a colleague, unrecoverable from another machine, and gone if the tab was closed.
 *
 * Three things about this API are worth knowing before using it:
 *
 *  - **The account is an id, not a name.** `customer` is the Salesforce AccountId and that is all
 *    the toolbox stores; the local `customer` table holds no account name on purpose. So a list of
 *    workshops comes back with ids, and the name shown next to one is fetched from Salesforce
 *    (`useCustomerNames` in `hooks/workshop-hooks.ts`). Searching by *name* is therefore a
 *    Salesforce query first, whose ids are then passed here as `customer`.
 *  - **State is one document, not rows.** `getWorkshopState` returns `{store_key: payload}` and
 *    `putWorkshopState` replaces the whole bag. That matches how SAFE works — the client owns the
 *    document and `POST db/safe/session/` stays stateless — and it means a store the SE emptied
 *    actually disappears. `patchWorkshopState` merges named stores instead, for a caller that owns
 *    only part of the bag.
 *  - **Progress is derived, never posted.** Every workshop carries a `progress` summary computed
 *    from its own state on read, so it cannot disagree with the state. Nothing here writes it.
 *
 * Ownership scopes the default list (`mine`), it does not restrict access: any SE can open and edit
 * a colleague's workshop, which is the entire point of storing it on the server.
 */
import { buildApiUrl, getApiHeaders } from '../queriesHelpers';

/* ---------------------------------------------------------------------- types */

export type WorkshopStatus =
  | 'draft'
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

export type WorkshopSectionStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'skipped';

/** Coverage buckets, as `progress.coverage` is keyed. Mirrors SAFE's capability statuses. */
export type WorkshopCoverage = Record<string, number>;

/**
 * What has been decided in a workshop, derived server-side from its state documents.
 *
 * Every field is a count over what the SE actually recorded — no score, and no readiness the
 * assessment has not been asked for. `ready` is the exception worth calling out: it is the same two
 * predicates the SAFE session gates its steps on, so a list row can tell a resumable workshop from
 * one that would open on its first step.
 */
export type WorkshopProgress = {
  /** True once any state has been written. A `draft` workshop has none. */
  started: boolean;
  /** When the state was last written, or null. ISO 8601. */
  stateUpdated: string | null;
  /** Which store keys exist, sorted. */
  stores: string[];
  bctsSelected: number;
  threatsSelected: number;
  capabilitiesTracked: number;
  /** Capabilities at Planned, Partial or Full. */
  capabilitiesAssessed: number;
  coverage: WorkshopCoverage;
  pinsSelected: number;
  thirdPartyProducts: number;
  customCapabilities: number;
  sectionsTotal: number;
  sectionsCompleted: number;
  ready: { risk: boolean; pins: boolean };
};

export type WorkshopType = {
  id: number;
  key: string;
  name: string;
  description?: string | null;
  journey_map?: number | null;
  active: boolean;
  /** Active sections on this type — the length of the delivery checklist. */
  section_count: number;
  created?: string;
  last_updated?: string;
  updated_by?: string | null;
};

export type Workshop = {
  id: number;
  /** The Salesforce AccountId. There is no account name in this payload; see the module comment. */
  customer: string;
  workshop_type: number;
  workshop_type_key: string;
  workshop_type_name: string;
  opportunity?: string | null;
  name?: string | null;
  status: WorkshopStatus;
  owner?: string | null;
  scheduled_for?: string | null;
  completed?: string | null;
  notes?: string | null;
  progress: WorkshopProgress;
  created?: string;
  last_updated?: string;
  updated_by?: string | null;
};

export type WorkshopSection = {
  id: number;
  workshop_type: number;
  key: string;
  name: string;
  description?: string | null;
  order: number;
  journey_map_item?: number | null;
  /** A `Resource` id, or null when the deck for this section has not been uploaded yet. */
  deck?: number | null;
  video?: number | null;
  active: boolean;
};

export type WorkshopSectionProgress = {
  id: number;
  workshop: number;
  section: number;
  section_key: string;
  section_name: string;
  section_order: number;
  status: WorkshopSectionStatus;
  notes?: string | null;
  custom_deck?: number | null;
  completed?: string | null;
  last_updated?: string;
  updated_by?: string | null;
};

/** One row of the delivery checklist: a section of the type, and this workshop's progress on it. */
export type WorkshopChecklistRow = {
  section: WorkshopSection;
  /** Null for a section nobody has touched — the common case on a new workshop. */
  progress: WorkshopSectionProgress | null;
};

/** The state bag: one named JSON document per store. Opaque here, exactly as it is server-side. */
export type WorkshopStateBag = Record<string, any>;

export type WorkshopStateResponse = {
  workshop: number;
  state: WorkshopStateBag;
  progress: WorkshopProgress;
};

export type WorkshopListFilters = {
  /** Defaults to true server-side: the caller's own workshops. Pass false for the team's. */
  mine?: boolean;
  /** One AccountId, or several comma-separated. */
  customer?: string;
  /** One workshop type key, or several comma-separated. */
  type?: string;
  /** One status, or several comma-separated. */
  status?: string;
  /** Matches the workshop name, the notes and the AccountId — never the account name. */
  search?: string;
  page?: number;
  size?: number;
};

export type WorkshopPage = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Workshop[];
};

export type CreateWorkshopRequest = {
  /** Salesforce AccountId. The local `customer` row is created on demand if it is unknown. */
  customer: string;
  /** A type key from `getWorkshopTypes`, e.g. `safe-risk`. */
  workshop_type: string;
  name?: string;
  notes?: string;
  /** ISO 8601. Supplying it creates the workshop as `scheduled` rather than `draft`. */
  scheduled_for?: string;
};

/** `owner`, `customer` and `workshop_type` are deliberately absent: none of them is editable. */
export type UpdateWorkshopRequest = {
  name?: string | null;
  notes?: string | null;
  scheduled_for?: string | null;
  opportunity?: string | null;
  status?: WorkshopStatus;
};

/* ---------------------------------------------------------------------- fetch */

const WORKSHOP_BASE = 'db/workshop';

const errorMessage = (payload: any, status: number, fallback: string): string => {
  const detail = payload?.detail ?? payload?.error ?? payload?.message;

  if (Array.isArray(detail) && detail.length) return String(detail[0]);
  if (detail) return String(detail);
  if (typeof payload === 'string' && payload.trim()) return payload;

  return `${fallback} (${status})`;
};

/**
 * All workshop requests go through here so they carry auth.
 *
 * `getApiHeaders` is not optional: a bare `fetch` at a relative `/api` path drops the toolbox token
 * in local development, where the app and the API are different origins, and the request comes back
 * 401 with no obvious cause. These routes also require a Salesforce session, so a 403 here usually
 * means the session lapsed rather than that the permission is missing.
 */
async function workshopFetch<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  endpoint: string,
  options: { params?: Record<string, any>; body?: any } = {},
): Promise<T> {
  const { params = {}, body } = options;

  const headers = { ...getApiHeaders(null, method, endpoint) } as Record<string, string>;

  const response = await fetch(buildApiUrl(endpoint, params, true), {
    method,
    headers,
    credentials: 'include',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await response.text();

  let parsed: any = null;

  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!response.ok) {
    const error: any = new Error(errorMessage(parsed, response.status, 'Workshop request failed'));
    error.status = response.status;
    error.payload = parsed;
    throw error;
  }

  return parsed as T;
}

/* ------------------------------------------------------------------ endpoints */

/** The types a workshop can be created as. Active only — a retired type is not offered. */
export async function getWorkshopTypes(): Promise<WorkshopType[]> {
  return await workshopFetch<WorkshopType[]>('GET', `${WORKSHOP_BASE}/types/`);
}

/**
 * A page of workshops.
 *
 * `mine` is omitted rather than sent as true when it is not set, so the server's own default
 * ("the caller's own") stays the single definition of that behaviour.
 */
export async function getWorkshops(
  filters: WorkshopListFilters = {},
): Promise<WorkshopPage> {
  const params: Record<string, any> = {};

  if (filters.mine !== undefined) params.mine = filters.mine ? 'true' : 'false';
  if (filters.customer) params.customer = filters.customer;
  if (filters.type) params.type = filters.type;
  if (filters.status) params.status = filters.status;
  if (filters.search) params.search = filters.search;
  if (filters.page) params.page = filters.page;
  if (filters.size) params.size = filters.size;

  const payload = await workshopFetch<any>('GET', `${WORKSHOP_BASE}/`, { params });

  // The list is paginated, but a caller should not have to care whether pagination was applied:
  // `P2PPageNumberPagination` answers with a bare array when the page size is not in play.
  if (Array.isArray(payload)) {
    return { count: payload.length, next: null, previous: null, results: payload };
  }

  return {
    count: payload?.count ?? 0,
    next: payload?.next ?? null,
    previous: payload?.previous ?? null,
    results: payload?.results ?? [],
  };
}

export async function getWorkshop(id: number): Promise<Workshop> {
  return await workshopFetch<Workshop>('GET', `${WORKSHOP_BASE}/${id}/`);
}

export async function createWorkshop(request: CreateWorkshopRequest): Promise<Workshop> {
  return await workshopFetch<Workshop>('POST', `${WORKSHOP_BASE}/`, { body: request });
}

export async function updateWorkshop(
  id: number,
  changes: UpdateWorkshopRequest,
): Promise<Workshop> {
  return await workshopFetch<Workshop>('PATCH', `${WORKSHOP_BASE}/${id}/`, { body: changes });
}

/** Delete a workshop and its state, progress and responses. Not reversible. */
export async function deleteWorkshop(id: number): Promise<void> {
  await workshopFetch<void>('DELETE', `${WORKSHOP_BASE}/${id}/`);
}

export async function getWorkshopState(id: number): Promise<WorkshopStateResponse> {
  return await workshopFetch<WorkshopStateResponse>('GET', `${WORKSHOP_BASE}/${id}/state/`);
}

/**
 * Replace the state bag.
 *
 * This is what SAFE calls after every session round trip: the session response hands back the
 * whole workshop document, and storing it whole is the only write that can also *remove* a store.
 */
export async function putWorkshopState(
  id: number,
  state: WorkshopStateBag,
): Promise<WorkshopStateResponse> {
  return await workshopFetch<WorkshopStateResponse>('PUT', `${WORKSHOP_BASE}/${id}/state/`, {
    body: { state },
  });
}

/** Merge named stores into the bag, leaving the rest alone. */
export async function patchWorkshopState(
  id: number,
  state: WorkshopStateBag,
): Promise<WorkshopStateResponse> {
  return await workshopFetch<WorkshopStateResponse>('PATCH', `${WORKSHOP_BASE}/${id}/state/`, {
    body: { state },
  });
}

/** The delivery checklist: every active section of the type, with this workshop's progress. */
export async function getWorkshopSections(id: number): Promise<WorkshopChecklistRow[]> {
  return await workshopFetch<WorkshopChecklistRow[]>('GET', `${WORKSHOP_BASE}/${id}/sections/`);
}

/** Record where a section got to. `section` is the section *key*, scoped to the workshop's type. */
export async function setWorkshopSectionStatus(
  id: number,
  change: { section: string; status?: WorkshopSectionStatus; notes?: string },
): Promise<WorkshopSectionProgress> {
  return await workshopFetch<WorkshopSectionProgress>(
    'PATCH',
    `${WORKSHOP_BASE}/${id}/sections/`,
    { body: change },
  );
}

/* ----------------------------------------------------------------- utilities */

export const WORKSHOP_STATUS_LABELS: Record<WorkshopStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const WORKSHOP_SECTION_STATUS_LABELS: Record<WorkshopSectionStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  completed: 'Completed',
  skipped: 'Skipped',
};

/**
 * What to call a workshop in a list.
 *
 * The name is optional — creating one from the dialog without typing a name is normal — so this
 * falls back to the type and then to the id. Never to the account name: the caller has that only
 * after Salesforce has answered, and a label that changes shape when a network call lands reads as
 * a bug.
 */
export function workshopLabel(workshop: Workshop): string {
  return workshop.name?.trim() || workshop.workshop_type_name || `Workshop ${workshop.id}`;
}

/** Which SAFE assessment a workshop type is, or null when the type is not a SAFE one. */
export function safeModeForType(typeKey: string | null | undefined): 'risk' | 'architect' | null {
  if (typeKey === 'safe-risk') return 'risk';
  if (typeKey === 'safe-architect') return 'architect';

  return null;
}
